import { checkPharmacyDataUpdateStatus } from 'app/services/pharmacyDataUpdate'
import { useCallback, useEffect, useRef, useState } from 'react'
import { DATASET, Dataset } from '../constants/dataset'
import { DATASET_OPTIONS } from '../constants/datasetOptions'
import { PHARMACY_DATA_UPDATE_STATUS } from '../constants/pharmacyDataUpdateStats'
import { UPLOAD_JOB_ID_KEY } from '../constants/storage'

type UpdateProgressStateProps = {
  jobId?: string
  dataset: Dataset
  name?: string
  onComplete: () => void
}

export function UpdateProgressState({
  jobId,
  dataset,
  name,
  onComplete,
}: UpdateProgressStateProps) {
  const pollingRef = useRef<number | null>(null)
  const [progressState, setProgressState] = useState<{
    state: string
    number: number
    message: string
  } | null>(null)

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      window.clearTimeout(pollingRef.current)
    }
    pollingRef.current = null
  }, [])

  const checkProgressState = useCallback(
    async (currentJobId: string, currentDataset: Dataset) => {
      if (!currentJobId) {
        stopPolling()
        return
      }

      try {
        const res = await checkPharmacyDataUpdateStatus(currentJobId)
        setProgressState({
          state: res.status,
          number: res.progress,
          message: res.message,
        })

        const shouldContinuePolling =
          res.status === PHARMACY_DATA_UPDATE_STATUS.STARTING ||
          res.status === PHARMACY_DATA_UPDATE_STATUS.STARTED ||
          res.status === PHARMACY_DATA_UPDATE_STATUS.STOPPING

        if (shouldContinuePolling) {
          pollingRef.current = window.setTimeout(() => {
            checkProgressState(currentJobId, currentDataset)
          }, 5000)
        } else {
          sessionStorage.removeItem(UPLOAD_JOB_ID_KEY)
          stopPolling()
          if (res.status === PHARMACY_DATA_UPDATE_STATUS.COMPLETED) {
            onComplete()
          }
        }
      } catch {
        stopPolling()
        setProgressState({
          state: 'ERROR',
          number: 0,
          message:
            'ステータスの取得に失敗しました。リロード後に再試行してください。',
        })
      }
    },
    [setProgressState, stopPolling, onComplete],
  )

  useEffect(() => {
    if (jobId && dataset && pollingRef.current === null) {
      checkProgressState(jobId, dataset)
    }
    return () => {
      if (pollingRef.current) {
        window.clearTimeout(pollingRef.current)
      }
    }
  }, [jobId, dataset, checkProgressState])

  return (
    <div className="flex gap-4 pb-4 flex-col items-center">
      {progressState && (
        <div className="text-lg font-bold text-blue-500">
          ステータス: {progressState.state}
          <div className="text-sm text-gray-500">
            <p>
              データセット:{' '}
              {dataset === DATASET.ALL
                ? '全件'
                : DATASET_OPTIONS.find(option => option.value === dataset)
                    ?.label}
            </p>
            {name && <p>ファイル名: {name}</p>}
          </div>
        </div>
      )}
    </div>
  )
}
